/**
 * SuperBrain — Forgot Password Page
 * Unauthenticated entry point; always shows the same generic success message
 * regardless of whether the email exists (no account enumeration).
 * © 2026 A-1 Affiliate Marketing LLC
 */

import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './AuthPage.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const r = await axios.post(`${API}/auth/forgot-password`, { email });
      setMessage(
        r?.data?.message ||
          'If an account exists for that email, a password reset link has been sent.'
      );
      setSubmitted(true);
    } catch (err) {
      // Backend is designed to never 4xx here for normal cases, so any error is
      // a real one (bad email format, network, etc.).
      const detail = err?.response?.data?.detail;
      setError(
        typeof detail === 'string'
          ? detail
          : 'Could not send reset email. Please check the address and try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page" data-testid="forgot-password-page">
      <div className="auth-container">
        <div className="auth-header">
          <h1>SuperBrain</h1>
          <p className="auth-subtitle">Account Recovery · Password Reset</p>
        </div>

        {!submitted && (
          <form className="auth-form" onSubmit={handleSubmit}>
            <p className="auth-help" style={{ marginBottom: '1.25rem' }}>
              Enter the email tied to your SuperBrain account. We&apos;ll send you a
              secure one-time link to choose a new password. The link expires
              in 1 hour.
            </p>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="your@email.com"
                data-testid="forgot-email-input"
              />
            </div>
            {error && (
              <div className="auth-error" data-testid="forgot-error">
                {error}
              </div>
            )}
            <button
              type="submit"
              className="auth-submit"
              disabled={loading || !email}
              data-testid="forgot-submit-btn"
            >
              {loading ? 'Sending…' : 'Send Reset Link'}
            </button>
            <button
              type="button"
              className="auth-link-btn"
              onClick={() => navigate('/')}
              data-testid="forgot-back-to-login"
            >
              ← Back to sign in
            </button>
          </form>
        )}

        {submitted && (
          <div className="auth-form" data-testid="forgot-success-panel">
            <div
              className="auth-info"
              data-testid="forgot-success-message"
              style={{ marginBottom: '1.5rem' }}
            >
              {message}
            </div>
            <p className="auth-help">
              Didn&apos;t receive it within a few minutes? Check your spam folder,
              or{' '}
              <button
                type="button"
                className="auth-link-btn"
                style={{ display: 'inline', padding: 0 }}
                onClick={() => {
                  setSubmitted(false);
                  setMessage('');
                }}
                data-testid="forgot-try-again-btn"
              >
                try a different email
              </button>
              .
            </p>
            <Link
              to="/"
              className="auth-submit"
              style={{
                display: 'block',
                textAlign: 'center',
                textDecoration: 'none',
                marginTop: '1rem',
              }}
              data-testid="forgot-done-btn"
            >
              Back to Sign In
            </Link>
          </div>
        )}

        <div className="auth-footer">
          <p>✝ GOD BLESS EVERYONE ✝</p>
          <p className="auth-copyright">© 2026 A-1 Affiliate Marketing LLC</p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
